import { useState } from "react"
import { Bot, Workflow } from "lucide-react"
import { cn } from "@/lib/utils"
import { AgentBuilderMockup } from "../mockups/agent-builder-mockup"
import { WorkflowBuilderMockup } from "../mockups/workflow-builder-mockup"
import type { AgentMockupConfig, WorkflowMockupConfig } from "../types"

interface PlatformShowcaseSectionProps {
  /** Agent builder configuration */
  agentConfig: AgentMockupConfig
  /** Workflow builder configuration */
  workflowConfig: WorkflowMockupConfig
}

/**
 * Platform showcase section with tabs for Agent Builder and Workflow Builder mockups.
 * Demonstrates how the use case is implemented on the platform.
 */
export function PlatformShowcaseSection({
  agentConfig,
  workflowConfig,
}: PlatformShowcaseSectionProps) {
  const [activeTab, setActiveTab] = useState<"agent" | "workflow">("agent")

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-b from-neutral-50 to-white">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-10 text-center">
          <h2 className="mb-4 text-2xl font-bold text-neutral-900 sm:text-3xl">
            See It in Action
          </h2>
          <p className="mx-auto max-w-2xl text-neutral-600">
            Explore how this use case is configured on the Taqat AI platform.
            Switch between the Agent Builder and Workflow Builder views.
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-8 flex justify-center">
          <div className="inline-flex rounded-xl bg-neutral-100 p-1.5">
            <button
              onClick={() => setActiveTab("agent")}
              className={cn(
                "flex items-center gap-2 rounded-lg px-6 py-2.5 text-sm font-medium transition-all duration-200",
                activeTab === "agent"
                  ? "bg-white text-neutral-900 shadow-sm"
                  : "text-neutral-600 hover:text-neutral-900"
              )}
            >
              <Bot className="h-4 w-4" />
              Agent Configuration
            </button>
            <button
              onClick={() => setActiveTab("workflow")}
              className={cn(
                "flex items-center gap-2 rounded-lg px-6 py-2.5 text-sm font-medium transition-all duration-200",
                activeTab === "workflow"
                  ? "bg-white text-neutral-900 shadow-sm"
                  : "text-neutral-600 hover:text-neutral-900"
              )}
            >
              <Workflow className="h-4 w-4" />
              Workflow Automation
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="relative">
          {/* Agent Builder Mockup */}
          <div
            className={cn(
              "transition-all duration-300",
              activeTab === "agent"
                ? "opacity-100"
                : "opacity-0 absolute inset-0 pointer-events-none"
            )}
          >
            <div className="mb-6">
              <p className="text-center text-sm text-neutral-600 max-w-2xl mx-auto">
                This AI agent is pre-configured with the right prompts, tools, and
                integrations to handle your specific use case. Customize as needed.
              </p>
            </div>
            <AgentBuilderMockup config={agentConfig} />
          </div>

          {/* Workflow Builder Mockup */}
          <div
            className={cn(
              "transition-all duration-300",
              activeTab === "workflow"
                ? "opacity-100"
                : "opacity-0 absolute inset-0 pointer-events-none"
            )}
          >
            <div className="mb-6">
              <p className="text-center text-sm text-neutral-600 max-w-2xl mx-auto">
                This workflow automates the end-to-end process, connecting triggers,
                AI agents, and actions into a seamless automation.
              </p>
            </div>
            <WorkflowBuilderMockup config={workflowConfig} />
          </div>

          {/* Decorative glow */}
          <div className="absolute -inset-4 -z-10 rounded-3xl bg-gradient-to-r from-primary/10 via-transparent to-secondary/10 opacity-50 blur-2xl" />
        </div>
      </div>
    </section>
  )
}
